import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { readingsApi } from "@/api";
import type { UpsertReadingData } from "@/api";
import { QUERY_KEYS } from "@/constants";

/** Darsning o'qish kontenti. 404 — "hali yo'q", xato emas. */
export const useReading = (lessonId: number | undefined) =>
  useQuery({
    queryKey: [QUERY_KEYS.GET_READING, lessonId],
    queryFn: () => readingsApi.getByLesson(lessonId as number),
    enabled: !!lessonId,
    retry: false,
    select: res => res.data,
  });

const useInvalidateReading = (lessonId: number, courseId?: number) => {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_READING, lessonId] });
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_COURSE_READINESS, courseId] });
  };
};

export const useUpsertReading = (lessonId: number, courseId?: number) => {
  const { t } = useTranslation();
  const invalidate = useInvalidateReading(lessonId, courseId);
  return useMutation({
    mutationFn: (data: UpsertReadingData) => readingsApi.upsert(lessonId, data),
    onSuccess: () => {
      invalidate();
      toast.success(t("COMMON.UPDATED"));
    },
  });
};

export const useAddReadingAttachments = (lessonId: number, courseId?: number) => {
  const { t } = useTranslation();
  const invalidate = useInvalidateReading(lessonId, courseId);
  return useMutation({
    mutationFn: (vars: { files: File[]; onProgress?: (percent: number) => void }) =>
      readingsApi.addAttachments(lessonId, vars.files, vars.onProgress),
    onSuccess: () => {
      invalidate();
      toast.success(t("COMMON.ADDED"));
    },
  });
};
